import type { ISettingsRepository, SettingsKey } from '@app/domain/ports';
import { Injector } from '@angular/core';
import { DekService } from '@app/domain/services';
import { CRYPTO_ADAPTER } from '@app/core/tokens/infrastructure.tokens';
import { DekError, ValidationError } from '@app/domain/entities';

/**
 * Decorator that adds encryption/decryption capabilities to a settings repository.
 *
 * Wraps the base repository and automatically encrypts/decrypts values
 * for keys with 'secure:' prefix. Performs encryption/decryption itself,
 * then delegates storage to the base repository.
 *
 * ## Sync vs Async Methods
 *
 * - **Sync methods**: Throw error if used with secure keys to prevent accidental misuse.
 * - **Async methods**: Handle encryption/decryption for secure keys, delegate for others.
 *
 * This implements the Decorator pattern, allowing encryption logic
 * to be added without modifying the base repository implementation.
 */
export class EncryptedSettingsRepositoryDecorator implements ISettingsRepository {
  constructor(
    private readonly baseRepository: ISettingsRepository,
    private readonly injector: Injector,
  ) {}

  // Synchronous methods - throw error for secure keys
  get<T>(key: SettingsKey): T | undefined;
  get<T>(key: SettingsKey, defaultValue: T): T;
  get<T>(key: SettingsKey, defaultValue?: T): T | undefined {
    if (this.requiresEncryption(key)) {
      throw new ValidationError(
        key,
        `Cannot use synchronous get() for secure key "${key}". ` +
          `Use getAsync() instead to handle decryption properly.`,
      );
    }
    return this.baseRepository.get(key, defaultValue);
  }

  set(key: SettingsKey, value: unknown): void {
    if (this.requiresEncryption(key)) {
      throw new ValidationError(
        key,
        `Cannot use synchronous set() for secure key "${key}". ` +
          `Use setAsync() instead to handle encryption properly.`,
      );
    }
    this.baseRepository.set(key, value);
  }

  remove(key: SettingsKey): void {
    this.baseRepository.remove(key);
  }

  // Asynchronous methods - handle encryption for secure keys
  async getAsync<T>(key: SettingsKey): Promise<T | undefined>;
  async getAsync<T>(key: SettingsKey, defaultValue: T): Promise<T>;
  async getAsync<T>(key: SettingsKey, defaultValue?: T): Promise<T | undefined> {
    if (this.requiresEncryption(key)) {
      // Get encrypted data from base repository
      const encrypted = await this.baseRepository.getAsync<{ iv: string; ciphertext: string }>(key);
      if (!encrypted) return defaultValue;

      // Decrypt the value
      const decrypted = await this.decrypt(encrypted);
      if (!decrypted) return defaultValue;

      try {
        return JSON.parse(decrypted) as T;
      } catch (error) {
        console.warn('Failed to parse decrypted value as JSON, returning as-is', error);
        // If not JSON, return as-is
        return decrypted as T;
      }
    }

    // Delegate to base repository for plain storage
    return this.baseRepository.getAsync<T>(key, defaultValue);
  }

  async setAsync(key: SettingsKey, value: unknown): Promise<void> {
    if (this.requiresEncryption(key)) {
      // Serialize the value
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);

      // Encrypt the value
      const encrypted = await this.encrypt(serialized);

      // Store encrypted value in base repository
      await this.baseRepository.setAsync(key, encrypted);
      return;
    }

    // Delegate to base repository for plain storage
    return this.baseRepository.setAsync(key, value);
  }

  async removeAsync(key: SettingsKey): Promise<void> {
    return this.baseRepository.removeAsync(key);
  }

  /**
   * Determines if a key requires encryption based on naming convention.
   * Keys with 'secure:' prefix are automatically encrypted.
   */
  private requiresEncryption(key: SettingsKey): boolean {
    return key.startsWith('secure:');
  }

  /**
   * Encrypt a string value using DEK.
   * Returns an object with base64-encoded iv and ciphertext.
   */
  private async encrypt(value: string): Promise<{ iv: string; ciphertext: string }> {
    const dekService = this.injector.get(DekService);
    const cryptoService = this.injector.get(CRYPTO_ADAPTER);
    const dekContainer = dekService.getDekContainer();

    if (!dekContainer.isUnlocked) {
      throw new DekError('store-setting', 'Application must be unlocked to store encrypted data');
    }

    let result: { iv: string; ciphertext: string } | undefined;

    await dekContainer.execute(async (dek) => {
      if (!dek) throw new DekError('store-setting', 'DEK is not available');
      const { ciphertext, iv } = await cryptoService.encryptString(value, dek);

      result = {
        iv: this.uint8ArrayToBase64(iv),
        ciphertext: this.uint8ArrayToBase64(ciphertext),
      };
    });

    if (!result) throw new DekError('store-setting', 'Encryption failed');
    return result;
  }

  /**
   * Decrypt a value using DEK.
   * Returns the decrypted string or undefined on failure.
   */
  private async decrypt(encrypted: { iv: string; ciphertext: string }): Promise<string | undefined> {
    const dekService = this.injector.get(DekService);
    const cryptoService = this.injector.get(CRYPTO_ADAPTER);
    const dekContainer = dekService.getDekContainer();

    if (!dekContainer.isUnlocked) {
      throw new DekError('retrieve-setting', 'Application must be unlocked to retrieve encrypted data');
    }

    let decrypted: string | undefined;

    await dekContainer.execute(async (dek) => {
      if (!dek) throw new DekError('retrieve-setting', 'DEK is not available');
      try {
        // Convert base64 back to Uint8Array
        const iv = this.base64ToUint8Array(encrypted.iv);
        const ciphertext = this.base64ToUint8Array(encrypted.ciphertext);

        decrypted = await cryptoService.decryptString(ciphertext, iv, dek);
      } catch (error) {
        console.warn(
          '[EncryptedSettingsRepository] Failed to decrypt setting (non-fatal — returning undefined):',
          error,
        );
        decrypted = undefined;
      }
    });

    return decrypted;
  }

  /**
   * Convert Uint8Array to base64 string (browser-compatible)
   */
  private uint8ArrayToBase64(bytes: Uint8Array): string {
    return btoa(String.fromCharCode(...bytes));
  }

  /**
   * Convert base64 string to Uint8Array (browser-compatible)
   */
  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
  }
}
